import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { DashboardLayout, ProtectedRoute } from "./components/DashboardLayout";
import { StorefrontLayout } from "./components/StorefrontLayout";
import { EmptyState } from "./components/ui";
import { CartProvider } from "./context/CartContext";
import { SessionProvider } from "./context/SessionContext";
import { StoreProvider } from "./context/StoreContext";
import { MemberProvider } from "./context/MemberContext";
import {
  DashboardHome,
} from "./pages/DashboardPages";
import { ReportsPage } from "./pages/ReportsPage";
import { MediaManagerPage } from "./pages/MediaManagerPage";
import { LoginPage } from "./pages/LoginPage";
import { AdminSettingsPage } from "./pages/AdminSettingsPage";
import { MemberPage } from "./pages/MemberPage";
import { MemberManagementPage } from "./pages/MemberManagementPage";
import {
  BranchAccessPage,
  WhatsAppSettingsPage,
} from "./pages/OperationsPages";
import {
  CategoryManagementPage,
  CustomerArchivePage,
  HomepageManagementPage,
  OwnerSecurityPage,
  PaymentMethodsPage,
  ProductCatalogManagementPage,
} from "./pages/CommercePages";
import { OrderManagementPage } from "./pages/OrderManagementPage";
import {
  CheckoutPage,
  InformationalPage,
  OrderLookupPage,
  OrderStatusPage,
} from "./pages/OrderPages";
import {
  CartPage,
  CategoryPage,
  HomePage,
  ProductDetailPage,
} from "./pages/StorePages";

const DeveloperMonitorApp = lazy(async () => {
  const module = await import("./developer-monitor/DeveloperMonitorApp");
  return { default: module.DeveloperMonitorApp };
});

function NotFoundPage() {
  return (
    <div className="page-shell inner-page">
      <EmptyState
        title="Halaman tidak ditemukan"
        description="Alamat yang Anda buka mungkin sudah berubah."
        action={
          <a className="button button--primary" href="/">
            Kembali ke Beranda
          </a>
        }
      />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function PublicSiteOpenTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname !== "sedapsayur.com" && hostname !== "www.sedapsayur.com") return;
    if (/^\/(?:admin|owner|developer|login)(?:\/|$)/u.test(pathname)) return;

    const businessDate = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Makassar",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    const storageKey = `sedapsayur-storefront-open:${businessDate}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) return;
      window.sessionStorage.setItem(storageKey, "pending");
    } catch {
      // Browser dengan storage diblokir tetap boleh membuka storefront.
    }

    void fetch("/api/public/analytics/open", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      credentials: "omit",
      cache: "no-store",
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) throw new Error("TRAFFIC_COUNTER_REJECTED");
        try {
          window.sessionStorage.setItem(storageKey, "recorded");
        } catch {
          // Tidak ada dampak ke pengalaman belanja.
        }
      })
      .catch(() => {
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch {
          // Counter bersifat non-kritis dan tidak boleh memblokir storefront.
        }
      });
  }, [pathname]);

  return null;
}

export default function App() {
  const location = useLocation();
  if (location.pathname.startsWith("/developer")) {
    return (
      <Suspense fallback={<main className="devmon-loading" aria-busy="true">Memuat Developer Monitor…</main>}>
        <DeveloperMonitorApp />
      </Suspense>
    );
  }
  return (
    <SessionProvider>
      <MemberProvider>
        <StoreProvider>
          <CartProvider>
          <ScrollToTop />
          <PublicSiteOpenTracker />
          <Routes>
            <Route element={<StorefrontLayout />}>
              <Route index element={<HomePage />} />
              <Route path="kategori/:slug" element={<CategoryPage />} />
              <Route path="produk/:slug" element={<ProductDetailPage />} />
              <Route path="keranjang" element={<CartPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="pesanan" element={<OrderLookupPage />} />
              <Route path="member" element={<MemberPage />} />
              <Route
                path="pesanan/:orderNumber"
                element={<OrderStatusPage />}
              />
              <Route
                path="tentang"
                element={<InformationalPage type="about" />}
              />
              <Route
                path="hubungi"
                element={<InformationalPage type="contact" />}
              />
              <Route
                path="kebijakan-privasi"
                element={<InformationalPage type="privacy" />}
              />
              <Route
                path="syarat-ketentuan"
                element={<InformationalPage type="terms" />}
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            <Route path="login" element={<LoginPage />} />

            <Route element={<ProtectedRoute role="ADMIN" />}>
              <Route path="admin" element={<DashboardLayout role="ADMIN" />}>
                <Route index element={<DashboardHome role="ADMIN" />} />
                <Route
                  path="pesanan"
                  element={<OrderManagementPage role="ADMIN" />}
                />
                <Route
                  path="produk"
                  element={<ProductCatalogManagementPage role="ADMIN" />}
                />
                <Route
                  path="stok"
                  element={<ProductCatalogManagementPage role="ADMIN" />}
                />
                <Route
                  path="pembayaran"
                  element={<OrderManagementPage role="ADMIN" paymentsOnly />}
                />
                <Route
                  path="media"
                  element={<MediaManagerPage role="ADMIN" />}
                />
                <Route
                  path="pelanggan"
                  element={<CustomerArchivePage role="ADMIN" />}
                />
                <Route path="member" element={<MemberManagementPage role="ADMIN" />} />
                <Route path="pengaturan" element={<AdminSettingsPage />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute role="OWNER" />}>
              <Route path="owner" element={<DashboardLayout role="OWNER" />}>
                <Route index element={<DashboardHome role="OWNER" />} />
                <Route path="cabang" element={<BranchAccessPage />} />
                <Route path="admin" element={<BranchAccessPage />} />
                <Route
                  path="pesanan"
                  element={<OrderManagementPage role="OWNER" />}
                />
                <Route
                  path="produk"
                  element={<ProductCatalogManagementPage role="OWNER" />}
                />
                <Route
                  path="kategori"
                  element={<CategoryManagementPage />}
                />
                <Route
                  path="stok"
                  element={<ProductCatalogManagementPage role="OWNER" />}
                />
                <Route path="pembayaran" element={<PaymentMethodsPage />} />
                <Route
                  path="pelanggan"
                  element={<CustomerArchivePage role="OWNER" />}
                />
                <Route path="member" element={<MemberManagementPage role="OWNER" />} />
                <Route path="tampilan" element={<HomepageManagementPage />} />
                <Route
                  path="media"
                  element={<MediaManagerPage role="OWNER" />}
                />
                <Route
                  path="laporan"
                  element={<ReportsPage />}
                />
                <Route path="pengaturan" element={<WhatsAppSettingsPage />} />
                <Route path="keamanan" element={<OwnerSecurityPage />} />
              </Route>
            </Route>
          </Routes>
          </CartProvider>
        </StoreProvider>
      </MemberProvider>
    </SessionProvider>
  );
}
